/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { Theme, makeStyles } from '@material-ui/core/styles';







import { Field, FieldProps, FieldRenderProps } from 'react-final-form';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */




import ConditionalContext from '../../context/ConditionalContext';
import DisableFormContext from '../../context/DisableFormContext';
import FieldContext from '../../context/FieldContext';
import Checkbox from './Fields/Checkbox';
import OpmsFieldErrorFlags from './OpmsFieldErrorFlags';
import NumericCheckbox from './Fields/NumericCheckbox';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */


interface IOpmsInput<
    ComponentType = any,
    FieldValue = any,
    RP extends FieldRenderProps<FieldValue, T> = FieldRenderProps<FieldValue, HTMLElement>,
    T extends HTMLElement = HTMLElement
> extends Omit<FieldProps<FieldValue, RP, T>, 'Name'>  {
    name?: string;
    component?: ComponentType
}

type OpmsInputComponentBase = keyof JSX.IntrinsicElements | React.JSXElementConstructor<any>;
type OpmsInputComponentType = OpmsInputComponentBase

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => {
    return {
        container: {
            display: 'flex'
        },
        field: {
            maxWidth: '100%',
            flex: '1 1 auto'
        }
    }
});


/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

export const pascalToCamelCase = (text: string): string => {
    if (!text) {
        return '';
    }

    return text.charAt(0).toLowerCase() + text.slice(1)
};


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

function OpmsInput<ComponentType extends OpmsInputComponentType, FieldValue = any, RP extends FieldRenderProps<FieldValue, T> = FieldRenderProps<FieldValue, HTMLElement>,  T extends HTMLElement = HTMLElement>({
    name,
    component,
    disabled,
    type,
    ...rest
}: IOpmsInput<ComponentType, FieldValue, RP, T> & Partial<React.ComponentProps<ComponentType>> & { disabled?: boolean }): React.ReactElement {
    const classes = useStyles();

    const fieldContext = React.useContext(FieldContext);

    const fieldName = React.useMemo(() => {
        if (name) {
            return name;
        }

        if (!fieldContext) {
            return '';
        }

        const splitFieldConst = fieldContext.split('.');

        return splitFieldConst.map(s => pascalToCamelCase(s)).join('.');

    }, [fieldContext, name]);

    const conditionalResult = React.useContext(ConditionalContext)
    const disableForm = React.useContext(DisableFormContext)

    const computeDisabled = React.useMemo(() => {
        return disableForm ?
            true :
            conditionalResult?.result === false ?
                true :
                disabled
    }, [disabled, conditionalResult?.result, disableForm])

    return (
        <div
            className={classes.container}
        >
            <div
                className={classes.field}
            >
                <Field
                    name={fieldName}
                    component={component as any}
                    type={type === undefined && component === Checkbox ? 'checkbox' : type}
                    {...rest}
                    disabled={computeDisabled}
                />
            </div>
            <OpmsFieldErrorFlags />
        </div>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default OpmsInput;
